"use client"

import { FC, useEffect, useCallback, useMemo } from "react"
import { usePathname } from "next/navigation"
import { useTranslations } from "next-intl"
import type { components } from "umbraco"
import { getMediaUrl } from "umbraco"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import CloseIcon from "../../icons/CloseIcon"
import ContactChannelGlyph from "../../common/ContactChannelGlyph"
import { resolveChannels } from "./channels"
import {
  Overlay,
  Panel,
  CloseButton,
  Header,
  LogoImage,
  TitleGroup,
  Title,
  Subtitle,
  CardList,
  ChannelCard,
  ChannelLabel,
} from "./styles"

type ContactModalProperties =
  components["schemas"]["ContactModalPropertiesModel"]

interface ContactModalProps {
  open: boolean
  onClose: () => void
  data: ContactModalProperties | null
}

const ContactModal: FC<ContactModalProps> = ({ open, onClose, data }) => {
  const t = useTranslations("Homepage")
  const pathname = usePathname()

  const channels = useMemo(() => resolveChannels(data), [data])

  // Body scroll lock while open
  useEffect(() => {
    if (!open) return
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [open])

  // Auto-close on route change
  useEffect(() => {
    if (open) onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // Esc to close
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [open, onClose])

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose()
    },
    [onClose],
  )

  if (!open || !data) return null

  const logoRel = getMediaUrl(data.logo)
  const logoSrc = logoRel ? getUmbracoMediaUrl(logoRel) : null

  return (
    <Overlay
      data-component="contact"
      onClick={handleOverlayClick}
      role="presentation"
    >
      <Panel role="dialog" aria-modal aria-label={t("contactModalAria")}>
        <CloseButton onClick={onClose} aria-label={t("closeContact")}>
          <CloseIcon />
        </CloseButton>

        <Header>
          {logoSrc && <LogoImage src={logoSrc} alt="" />}
          {(data.title || data.subtitle) && (
            <TitleGroup>
              {data.title && <Title variant="h3">{data.title}</Title>}
              {data.subtitle && (
                <Subtitle variant="body1">{data.subtitle}</Subtitle>
              )}
            </TitleGroup>
          )}
        </Header>

        {channels.length > 0 && (
          <CardList>
            {channels.map((channel) => (
              <ChannelCard
                key={channel.key}
                href={channel.href}
                aria-label={channel.label || channel.channelType}
                {...(channel.external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
              >
                <ContactChannelGlyph
                  channelType={channel.channelType}
                  iconUrl={channel.iconUrl}
                  size={40}
                />
                <ChannelLabel variant="body1">{channel.label}</ChannelLabel>
              </ChannelCard>
            ))}
          </CardList>
        )}
      </Panel>
    </Overlay>
  )
}

export default ContactModal
